import type { WebGLRenderer } from 'three'
import type { GraphicsBackend } from '@/graphics/GraphicsBackend'
import type { ITilePreparation } from '@/plugins/tiles/contracts'
import { TilePreparationCapacity } from '@/plugins/tiles/runtime/TilePreparationCapacity'
import { TilesError } from '@/plugins/tiles/TilesError'

export interface TilePreparationOptions {
  /** 同一 renderer 一次真实帧执行的软额度，默认 3 ms。 */
  readonly maxMs?: number
  readonly maxUploadBytes?: number
  readonly maxFirstVisibleContents?: number
  readonly highWaterBytes?: number
  readonly lowWaterBytes?: number
}

interface PreparationJob {
  readonly owner: object
  readonly task: ITilePreparation
  readonly wake: () => void
  readonly complete: (error?: unknown) => void
  readonly priority: () => number
  lastStep: number
  waiting: boolean
  cancelled: boolean
  settled: boolean
}

const coordinators = new WeakMap<object, TilePreparationCoordinator>()

/** 全 renderer/backend 共享执行额度；所有 Record 的逻辑任务与租约仍独立。 */
export class TilePreparationCoordinator {
  readonly output: TilePreparationCapacity
  private readonly _jobs: PreparationJob[] = []
  private readonly _owners = new Map<object, () => void>()
  private _execution: object | number | undefined
  private _spentMs = 0
  private _uploadedBytes = 0
  private _firstVisible = 0
  private readonly _firstDrawJobs = new Set<PreparationJob>()
  private _dispatchSerial = 0
  private _generation = 0
  private _lost = false
  private _lastOverrun: Readonly<{
    ms: number
    bytes: number
    mode: 'incremental' | 'legacy'
    kind: string
  }> | null = null
  private _phaseMs = { upload: 0, compile: 0, firstDraw: 0, legacy: 0 }
  private readonly _maxMs: number
  private readonly _maxUploadBytes: number
  private readonly _maxFirstVisible: number

  constructor(
    options: TilePreparationOptions = {},
    private readonly _now = () => performance.now()
  ) {
    this.output = new TilePreparationCapacity(options.highWaterBytes, options.lowWaterBytes)
    this._maxMs = options.maxMs ?? 3
    this._maxUploadBytes = options.maxUploadBytes ?? 16 * 1024 ** 2
    this._maxFirstVisible = options.maxFirstVisibleContents ?? 2
    if (
      !Number.isFinite(this._maxMs) ||
      this._maxMs < 1 ||
      this._maxMs > 8 ||
      !Number.isSafeInteger(this._maxUploadBytes) ||
      this._maxUploadBytes < 1 ||
      !Number.isSafeInteger(this._maxFirstVisible) ||
      this._maxFirstVisible < 1
    )
      throw new RangeError('Tiles 准备额度无效（时间范围 1..8ms，字节/首次显示数为正整数）')
  }

  static forGraphics(
    graphics: GraphicsBackend,
    options?: TilePreparationOptions
  ): TilePreparationCoordinator {
    let coordinator = coordinators.get(graphics)
    if (!coordinator) {
      coordinator = new TilePreparationCoordinator(options)
      coordinators.set(graphics, coordinator)
    }
    return coordinator
  }

  static forRenderer(
    renderer: WebGLRenderer,
    options?: TilePreparationOptions
  ): TilePreparationCoordinator {
    let coordinator = coordinators.get(renderer)
    if (!coordinator) {
      coordinator = new TilePreparationCoordinator(options)
      coordinators.set(renderer, coordinator)
      renderer.domElement?.addEventListener('webglcontextlost', () =>
        coordinator!.setContextLost(true)
      )
      renderer.domElement?.addEventListener('webglcontextrestored', () =>
        coordinator!.setContextLost(false)
      )
    }
    return coordinator
  }

  get generation(): number {
    return this._generation
  }

  register(owner: object, invalidate: () => void): () => void {
    this._owners.set(owner, invalidate)
    return () => {
      this.cancelOwner(owner, true)
      this._owners.delete(owner)
    }
  }

  enqueue(
    owner: object,
    task: ITilePreparation,
    wake: () => void,
    complete: (error?: unknown) => void,
    priority: () => number = () => 0
  ): void {
    this._jobs.push({
      owner,
      task,
      wake,
      complete,
      priority,
      lastStep: this._dispatchSerial,
      waiting: false,
      cancelled: false,
      settled: false,
    })
  }

  hasImmediateWork(owner: object): boolean {
    return (
      !this._lost && this._jobs.some((job) => job.owner === owner && !job.waiting && !job.cancelled)
    )
  }

  beginExecution(execution: object | number): void {
    if (this._execution === execution) return
    this._execution = execution
    this._spentMs = 0
    this._uploadedBytes = 0
    this._firstVisible = 0
    this._firstDrawJobs.clear()
    this._phaseMs = { upload: 0, compile: 0, firstDraw: 0, legacy: 0 }
  }

  /** 多视图重复进入仍共用本次额度；完成帧号相同的另一执行应传入新身份。 */
  run(execution: object | number): void {
    this.beginExecution(execution)
    if (this._lost) return
    let steps = 0
    while (
      this._spentMs < this._maxMs &&
      this._uploadedBytes < this._maxUploadBytes &&
      steps < 1024
    ) {
      // 覆盖请求优先，但优先级差最多四步；等待年龄最终压过持续到来的细化请求。
      let index = -1
      let highest = Number.NEGATIVE_INFINITY
      for (let i = 0; i < this._jobs.length; i++) {
        const job = this._jobs[i]
        if (
          job.waiting ||
          job.cancelled ||
          (job.task.nextStep === 'first-draw' &&
            !this._firstDrawJobs.has(job) &&
            this._firstVisible >= this._maxFirstVisible)
        )
          continue
        const priority = job.priority()
        const score =
          Math.max(-4, Math.min(0, Number.isFinite(priority) ? priority : 0)) +
          Math.min(8, this._dispatchSerial - job.lastStep)
        if (score > highest) {
          highest = score
          index = i
        }
      }
      if (index === -1) break
      const job = this._jobs.splice(index, 1)[0]
      job.lastStep = ++this._dispatchSerial
      const started = this._now()
      const kind = job.task.nextStep ?? 'legacy'
      if (kind === 'first-draw' && !this._firstDrawJobs.has(job)) {
        this._firstDrawJobs.add(job)
        this._firstVisible++
      }
      let bytes = 0
      try {
        const step = job.task.step()
        if (step.state === 'ready') {
          bytes = step.uploadedBytes ?? 0
          this.finish(job)
        } else {
          if (step.state === 'pending') bytes = step.uploadedBytes
          else {
            job.waiting = true
            void step.completion.then(
              () => {
                if (job.settled) return
                job.waiting = false
                if (job.cancelled) this.finish(job)
                else if (job.task.mode === 'legacy') this.finish(job)
                else job.wake()
              },
              (error) => this.finish(job, error)
            )
          }
          this._jobs.push(job) // 一步后轮转，不能由单瓦片独占所有上传。
        }
      } catch (error) {
        this.finish(job, error)
      }
      const elapsed = this._now() - started
      this._spentMs += elapsed
      if (kind !== 'ready') this._phaseMs[kind === 'first-draw' ? 'firstDraw' : kind] += elapsed
      this._uploadedBytes += bytes
      if (elapsed > this._maxMs)
        this._lastOverrun = Object.freeze({ ms: elapsed, bytes, mode: job.task.mode, kind })
      steps++
    }
  }

  cancelOwner(owner: object, force = false): void {
    for (const job of [...this._jobs]) {
      if (job.owner !== owner) continue
      job.cancelled = true
      try {
        job.task.cancel(force)
      } catch {
        /* 回收单项失败不能遗留其余任务。 */
      }
      // 编译尚未真正结束时，继续保留调用者的资源租约。
      if (!job.waiting || (force && job.task.mode === 'incremental')) this.finish(job)
    }
  }

  getStatus() {
    return Object.freeze({
      generation: this._generation,
      contextLost: this._lost,
      queued: this._jobs.filter((job) => !job.waiting).length,
      waiting: this._jobs.filter((job) => job.waiting).length,
      spentMs: this._spentMs,
      uploadedBytes: this._uploadedBytes,
      phaseMs: Object.freeze({ ...this._phaseMs }),
      firstDraws: this._firstVisible,
      lastOverrun: this._lastOverrun,
    })
  }

  private setContextLost(lost: boolean): void {
    this._lost = lost
    this._generation++
    for (const job of [...this._jobs]) this.cancelOwner(job.owner, lost)
    for (const invalidate of this._owners.values()) invalidate()
  }

  private finish(job: PreparationJob, error?: unknown): void {
    if (job.settled) return
    job.settled = true
    this._firstDrawJobs.delete(job)
    const index = this._jobs.indexOf(job)
    if (index !== -1) this._jobs.splice(index, 1)
    try {
      job.task.cancel()
    } catch (cleanupError) {
      error ??= cleanupError
    }
    try {
      job.complete(
        error ??
          (job.cancelled
            ? new TilesError({
                code: 'cancelled',
                stage: 'runtime',
                message: '准备任务已取消',
              })
            : undefined)
      )
    } catch {
      /* 一个拥有者退出不能阻止共享队列继续清理。 */
    }
  }
}
